/**
 * Sprint 8 — minimal branding editor. Lets a consultant change the
 * org's primary_color (a hex string). The color appears as a 6pt rule
 * + meta-label tint on the PDF report cover; nowhere else by user
 * direction.
 *
 * Sprint 9 — extended with a logo upload section. The uploaded image
 * replaces the 6pt color rule on the PDF cover when set; the rest of
 * the cover (title, meta labels) continues to use the primary color.
 *
 * Sprint 9 refactor — extracted out of Dashboard.jsx (was 583 lines).
 * The Dashboard now imports this component instead of defining it inline.
 */
import React from "react";

export default function BrandingModal({ org, onClose, onSaveColor, onUploadLogo, onClearLogo, onDone }) {
  const [color, setColor] = React.useState(org.primary_color);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [logoError, setLogoError] = React.useState("");
  const fileInputRef = React.useRef(null);
  const hexValid = /^#[0-9A-Fa-f]{6}$/.test(color);
  const dirty = color.toUpperCase() !== (org.primary_color || "").toUpperCase();

  async function submit(e) {
    e.preventDefault();
    if (!hexValid) {
      setError("Use a six-digit hex color, e.g. #5C3A8B.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (dirty) await onSaveColor(color);
      onDone();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Couldn't save");
      setSaving(false);
    }
  }

  async function handleLogoFile(file) {
    if (!file) return;
    setLogoError("");
    // Mirror the backend's hard cap so we fail fast in the UI.
    const MAX = 1 * 1024 * 1024;
    if (file.size > MAX) {
      setLogoError(`Logo file is too large. Max size is ${MAX / 1024} KB.`);
      return;
    }
    const allowed = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      setLogoError("Use PNG, JPEG, WebP, or SVG.");
      return;
    }
    setLogoUploading(true);
    try {
      await onUploadLogo(file);
    } catch (err) {
      setLogoError(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleClearLogo() {
    setLogoError("");
    setLogoUploading(true);
    try {
      await onClearLogo();
    } catch (err) {
      setLogoError(err?.response?.data?.detail || err.message || "Couldn't remove logo");
    } finally {
      setLogoUploading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(26,26,26,0.4)" }}
      data-testid="branding-modal"
      onClick={onClose}
    >
      <div
        className="bg-paper p-8 w-full max-w-md"
        style={{ border: "1px solid #DDDDDD" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow mb-2">Client branding</div>
        <h3 className="text-xl mb-1" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
          {org.name}
        </h3>
        <p className="text-sm text-gray-700 mb-5 max-w-prose">
          Sets the accent color and logo on the cover page of this client&rsquo;s
          PDF report. The consultant console stays neutral; only the PDF
          picks up the branding.
        </p>

        {/* ------------------------------------------------ Logo */}
        <div className="mb-6 pb-6" style={{ borderBottom: "1px solid #EEEEEE" }}>
          <div className="label mb-2">Logo</div>
          <div className="flex items-center gap-4 mb-3">
            <div
              style={{
                width: 96, height: 48, background: "white",
                border: "1px solid #DDDDDD", display: "flex",
                alignItems: "center", justifyContent: "center",
                padding: 4,
              }}
              data-testid="branding-logo-preview"
            >
              {org.logo_url ? (
                <img
                  src={org.logo_url}
                  alt={`${org.name} logo preview`}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              ) : (
                <span className="text-xs text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                  NO LOGO
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={(e) => handleLogoFile(e.target.files?.[0])}
                disabled={logoUploading}
                data-testid="branding-logo-upload"
                style={{ fontSize: 12 }}
              />
              {org.logo_url && (
                <button
                  type="button"
                  onClick={handleClearLogo}
                  disabled={logoUploading}
                  data-testid="branding-logo-remove"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                    fontSize: "10px", color: "#AD393E", background: "transparent",
                    border: "1px solid #AD393E", padding: "6px 10px",
                    cursor: logoUploading ? "wait" : "pointer", alignSelf: "flex-start",
                  }}
                >
                  {logoUploading ? "Working…" : "Remove logo"}
                </button>
              )}
            </div>
          </div>
          {logoError && (
            <div className="text-xs mt-2" style={{ color: "#AD393E" }} data-testid="branding-logo-error">
              {logoError}
            </div>
          )}
          <div className="text-xs text-gray-500 mt-2">
            PNG, JPEG, WebP, or SVG up to 1 MB. Replaces the color rule on the PDF cover.
          </div>
        </div>

        {/* ------------------------------------------------ Color */}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label" htmlFor="branding-color-text">Primary color (hex)</label>
            <div className="flex items-center gap-3">
              <input
                id="branding-color-picker"
                type="color"
                value={hexValid ? color : "#4592A8"}
                onChange={(e) => { setColor(e.target.value.toUpperCase()); setError(""); }}
                data-testid="branding-color-picker"
                aria-label="Primary color picker"
                style={{ width: 48, height: 38, padding: 0, border: "1px solid #DDDDDD", background: "white" }}
              />
              <input
                id="branding-color-text"
                type="text"
                value={color}
                onChange={(e) => { setColor(e.target.value); setError(""); }}
                placeholder="#5C3A8B"
                data-testid="branding-color-text"
                style={{
                  flex: 1, padding: "10px 12px", border: "1px solid #DDDDDD",
                  background: "white", fontSize: 14, fontFamily: "Oswald", letterSpacing: "0.08em",
                }}
              />
            </div>
            {!hexValid && (
              <div className="text-xs text-gray-600 mt-2" data-testid="branding-error">
                Use a six-digit hex color, e.g. #5C3A8B.
              </div>
            )}
          </div>
          {error && (
            <div className="text-sm" style={{ color: "#AD393E" }} data-testid="branding-error">{error}</div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-quiet"
              data-testid="branding-cancel"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={saving || !hexValid}
              data-testid="branding-save"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                fontSize: "11px", color: "#FBFAF7",
                background: !hexValid ? "#999898" : "#1A1A1A",
                border: 0, padding: "10px 18px",
                cursor: saving ? "wait" : (!hexValid ? "not-allowed" : "pointer"),
              }}
            >
              {saving ? "Saving…" : (dirty ? "Save branding" : "Done")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
